import { remark } from 'remark'
import remarkBreaks from 'remark-breaks'
import remarkGfm from 'remark-gfm'
import remarkHtml from 'remark-html'
import remarkStringify from 'remark-stringify'
import type { Node } from 'unist'

interface MarkdownNode extends Node {
    type: string
    children?: MarkdownNode[]
    value?: string
    url?: string
}

interface TextNode extends MarkdownNode {
    type: 'text'
    value: string
}

export interface FaqButton {
    label: string
    type: 'external' | 'internal'
    href: string
}

export interface FaqContentSegment {
    type: 'text' | 'button'
    content?: string
    button?: FaqButton
}

export interface ParsedFaqContent {
    content: string
    button?: FaqButton
    segments: FaqContentSegment[]
    html: string
}

function remarkFaqButtons() {
    return (tree: MarkdownNode) => {
        function visit(node: MarkdownNode): void {
            if (node.type === 'paragraph' && node.children) {
                const newChildren: MarkdownNode[] = []

                for (const child of node.children) {
                    if (child.type === 'link' && child.url?.startsWith('button:')) {
                        const textChild = child.children?.[0] as TextNode
                        const buttonText = textChild?.value || 'Button'

                        const buttonNode: MarkdownNode = {
                            type: 'html',
                            value: `<button>${buttonText}</button>`,
                        }
                        newChildren.push(buttonNode)
                    } else {
                        newChildren.push(child)
                    }
                }

                node.children = newChildren
            }

            if (node.children) {
                node.children.forEach(visit)
            }
        }

        visit(tree)
    }
}

function remarkStyleLinks() {
    return (tree: MarkdownNode) => {
        function visit(node: MarkdownNode): void {
            if (node.type === 'link') {
                const linkNode = node as unknown as {
                    data?: { hProperties?: Record<string, unknown> }
                }
                linkNode.data = linkNode.data || {}
                linkNode.data.hProperties = {
                    ...(linkNode.data.hProperties || {}),
                    class: 'underline text-black',
                    target: '_blank',
                }
            }

            if (node.children) {
                node.children.forEach(visit)
            }
        }

        visit(tree)
    }
}

export async function markdownToHtml(markdown: string): Promise<string> {
    const result = await remark()
        .use(remarkBreaks)
        .use(remarkGfm)
        .use(remarkFaqButtons)
        .use(remarkStyleLinks)
        .use(remarkHtml, { sanitize: false })
        .process(markdown)

    return String(result)
}

export async function parseFaqMarkdown(markdown: string): Promise<ParsedFaqContent> {
    const buttonRegex = /\[([^\]]+)\]\(button:(external|internal):([^)]+)\)/g

    const segments: FaqContentSegment[] = []
    let lastIndex = 0
    let firstButton: FaqButton | undefined
    let match

    while ((match = buttonRegex.exec(markdown)) !== null) {
        const [fullMatch, label, type, href] = match

        if (match.index > lastIndex) {
            const textContent = markdown.slice(lastIndex, match.index).trim()
            if (textContent) {
                segments.push({
                    type: 'text',
                    content: textContent,
                })
            }
        }

        const button: FaqButton = {
            label,
            type: type as 'external' | 'internal',
            href,
        }

        if (!firstButton) {
            firstButton = button
        }

        segments.push({
            type: 'button',
            button,
        })

        lastIndex = match.index + fullMatch.length
    }

    if (lastIndex < markdown.length) {
        const remainingContent = markdown.slice(lastIndex).trim()
        if (remainingContent) {
            segments.push({
                type: 'text',
                content: remainingContent,
            })
        }
    }

    if (segments.length === 0) {
        segments.push({
            type: 'text',
            content: markdown,
        })
    }

    const html = await markdownToHtml(markdown)

    const cleanContent = markdown.replace(/\[([^\]]+)\]\(button:[^)]+\)/g, '').trim()

    return {
        content: cleanContent,
        button: firstButton,
        segments,
        html,
    }
}

export async function markdownToText(markdown: string): Promise<string> {
    let text = markdown.replace(/\[([^\]]+)\]\(button:[^)]+\)/g, '')

    const result = await remark().use(remarkStringify).process(text)

    text = String(result)
        .replace(/\*\*(.*?)\*\*/g, '$1')
        .replace(/\*(.*?)\*/g, '$1')
        .replace(/`(.*?)`/g, '$1')
        .replace(/\[([^\]]+)\]\([^)]+\)/g, '$1')
        .replace(/#+\s*/g, '')
        .replace(/\n\n+/g, '\n')
        .trim()

    return text
}
